import React, { useState, useEffect } from 'react';
import { CardComponentProps } from '@/lib/cards';
import { getLatestDigest, previewDigest } from '@/api/digest';

/**
 * Latest Digest Card
 * Shows the latest digest with preview action
 */
export const LatestDigestCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const result = await getLatestDigest();
        setGeneratedAt(result.generatedAt);
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [onError]);

  const handleAction = async (action: string) => {
    try {
      setActionInProgress(true);
      if (action === 'Preview') {
        const result = await previewDigest();
        setPreviewHtml(result.html);
      }
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionInProgress(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '1rem', color: '#666' }}>Loading digest...</div>;
  }

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        {generatedAt ? (
          <div style={{ marginBottom: '0.5rem' }}>
            <strong>Generated:</strong> {new Date(generatedAt).toLocaleString()}
          </div>
        ) : (
          <div style={{ color: '#666', fontSize: '0.9rem' }}>No digest has been sent yet.</div>
        )}
      </div>

      <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
        {card.cardActionsList.map((action: string) => (
          <button
            key={action}
            onClick={() => handleAction(action)}
            disabled={actionInProgress}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: actionInProgress ? 'not-allowed' : 'pointer',
              opacity: actionInProgress ? 0.6 : 1,
            }}
          >
            {action}
          </button>
        ))}
      </div>

      {previewHtml && (
        <div
          style={{
            padding: '1rem',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            marginTop: '1rem',
            maxHeight: '400px',
            overflow: 'auto',
          }}
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
      )}
    </div>
  );
};

LatestDigestCard.displayName = 'LatestDigestCard';

export default LatestDigestCard;
